import { Badge, BlockStack, Box, Card, InlineStack, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'
import type { ComparisonMetric } from '../../sdk/exec.api'

// Which metrics to surface and how to label them
const MOMENTUM_METRICS: { id: string; label: string; lowerIsBetter?: boolean }[] = [
  { id: 'fc.delivery_rate',     label: 'Delivery rate'    },
  { id: 'fc.return_rate',       label: 'Return rate',      lowerIsBetter: true },
  { id: 'cf.confirmation_rate', label: 'Confirmation rate' },
  { id: 'fc.avg_order_value',   label: 'Avg order value'  },
]

function fmt(m: ComparisonMetric): { current: string; previous: string; delta: string } {
  const isRevenue = m.unit === 'EGP' || m.id.includes('revenue') || m.id.includes('value')
  const f = (n: number | null) => {
    if (n == null) return '—'
    if (isRevenue) {
      if (n >= 1000) return `${(n / 1000).toFixed(0)}K`
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    }
    return `${n.toFixed(1)}%`
  }
  const deltaSign = (m.delta ?? 0) > 0 ? '+' : ''
  return {
    current:  f(m.current),
    previous: f(m.previous),
    delta:    `${deltaSign}${f(m.delta)}`,
  }
}

type Props = { data: BccData }

export function BusinessMomentum({ data }: Props) {
  const { comparison } = data
  const metricMap = new Map<string, ComparisonMetric>(
    comparison.metrics.map(m => [m.id, m])
  )

  // Overall trend: count how many key metrics improved
  type MomentumItem = { label: string; metric: ComparisonMetric; improved: boolean; lowerIsBetter?: boolean }
  const items: MomentumItem[] = MOMENTUM_METRICS.flatMap(({ id, label, lowerIsBetter }) => {
    const m = metricMap.get(id)
    if (!m) return []
    const improved = lowerIsBetter
      ? (m.delta ?? 0) < 0
      : (m.delta ?? 0) > 0
    return [{ label, metric: m, improved, lowerIsBetter }]
  })

  const improvedCount = items.filter(i => i.improved).length
  const overallImproving = improvedCount >= Math.ceil(items.length / 2)

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Business Momentum</Text>
          <Badge tone={overallImproving ? 'success' : 'critical'}>
            {overallImproving ? '↑ Improving' : '↓ Declining'}
          </Badge>
        </InlineStack>

        <Text as="p" variant="bodySm" tone="subdued">
          Last 14 days vs previous 14 days
        </Text>

        <BlockStack gap="200">
          {items.map(({ label, metric, improved, lowerIsBetter }) => {
            const { current, previous, delta } = fmt(metric)
            const isPositive = lowerIsBetter ? !improved : improved
            const arrow = (metric.delta ?? 0) > 0 ? '↑' : (metric.delta ?? 0) < 0 ? '↓' : '→'
            const tone = metric.delta === 0 || metric.delta == null
              ? undefined
              : isPositive ? 'success' : 'critical'

            return (
              <Box
                key={metric.id}
                background="bg-surface-secondary"
                padding="300"
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="p" variant="bodySm">{label}</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="p" variant="bodySm" tone="subdued">{previous}</Text>
                    <Text as="p" variant="bodySm" tone={tone} fontWeight="semibold">
                      {arrow} {current}
                    </Text>
                    <Text as="p" variant="bodySm" tone={tone}>
                      ({delta})
                    </Text>
                  </InlineStack>
                </InlineStack>
              </Box>
            )
          })}
        </BlockStack>
      </BlockStack>
    </Card>
  )
}
